import * as React from 'react';
import styled from 'styled-components';
import { Heading, Body } from '@lendi-ui/typography';
import { map } from '@lendi-ui/breakpoint';
import { deriveSize, normalise } from '@lendi-ui/utils';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { pt, mb } from '@lendi-ui/spacing';
import HighlightOff from '@lendi-ui/icon/HighlightOff';
import QuestionOutline from '@lendi-ui/icon/QuestionOutline';
import cuid from 'cuid';
import isChromatic from 'chromatic/isChromatic';
import Tooltip from '@lendi-ui/tooltip';

function _toPrimitive(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}

function _toPropertyKey(arg) {
  var key = _toPrimitive(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}

var bodyStyleBySizeMixin = size => map(size, val => {
  switch (val) {
    case 'sm':
      return "\n          font-size: ".concat(deriveSize(0.75), ";\n      ");
    case 'lg':
      return "\n          font-size: ".concat(deriveSize(1), ";\n      ");
    default:
      return undefined;
  }
});
var headingStyleBySizeMixin = size => map(size, val => {
  switch (val) {
    case 'sm':
      return "\n          font-size: ".concat(deriveSize(1), ";\n          margin-right: ").concat(deriveSize(0.5), ";\n      ");
    case 'lg':
      return "\n          font-size: ".concat(deriveSize(1.75), ";\n          margin-right: ").concat(deriveSize(1), ";\n      ");
    default:
      return undefined;
  }
});
var LabelHeading = styled(Heading).withConfig({
  displayName: "indexstyle__LabelHeading",
  componentId: "lui__sc-1djucs9-0"
})(["", " display:inline-block;"], _ref => {
  var {
    size
  } = _ref;
  return headingStyleBySizeMixin(size);
});
var AssistiveBody = styled(Body).withConfig({
  displayName: "indexstyle__AssistiveBody",
  componentId: "lui__sc-1djucs9-1"
})(["", ";"], _ref2 => {
  var {
    size
  } = _ref2;
  return bodyStyleBySizeMixin(size);
});

var Label = _ref => {
  var {
    size,
    label,
    assistiveText,
    link,
    isRequired
  } = _ref;
  return /*#__PURE__*/jsxs("div", {
    children: [/*#__PURE__*/jsxs(LabelHeading, {
      size: size,
      children: [label, !isRequired && ' (Optional)']
    }), link, /*#__PURE__*/jsx(AssistiveBody, {
      color: "shade.400",
      size: size,
      children: assistiveText
    })]
  });
};

var Wrapper = styled.div.withConfig({
  displayName: "indexstyle__Wrapper",
  componentId: "lui__sc-1ij5id7-0"
})(["width:100%;display:flex;flex-direction:row;justify-content:flex-start;align-items:base-line;", ";color:", ";"], pt('xxxs'), _ref => {
  var {
    theme
  } = _ref;
  return theme.color.interaction.error;
});
var StyledError = styled(Body).withConfig({
  displayName: "indexstyle__StyledError",
  componentId: "lui__sc-1ij5id7-1"
})(["color:", ";"], _ref2 => {
  var {
    theme
  } = _ref2;
  return theme.color.interaction.error;
});

var ErrorMessage = _ref => {
  var {
    error
  } = _ref;
  return /*#__PURE__*/jsxs(Wrapper, {
    children: [/*#__PURE__*/jsx(HighlightOff, {}), /*#__PURE__*/jsx(StyledError, {
      size: "xs",
      ml: "xxxs",
      children: error
    })]
  });
};

var FieldWrapper = styled.div.withConfig({
  displayName: "indexstyle__FieldWrapper",
  componentId: "lui__sc-1pu8jrh-0"
})(["", ";display:flex;flex-direction:column;width:100%;"], normalise);
var LabelField = styled.label.withConfig({
  displayName: "indexstyle__LabelField",
  componentId: "lui__sc-1pu8jrh-1"
})(["width:100%;display:flex;align-items:center;", ";"], mb('xxxs'));
var QuestionIcon = styled(QuestionOutline).withConfig({
  displayName: "indexstyle__QuestionIcon",
  componentId: "lui__sc-1pu8jrh-2"
})(["display:flex;color:", ";"], _ref => {
  var {
    theme
  } = _ref;
  return theme.color.text.contrast;
});

var _excluded = ["size", "label", "isRequired", "assistiveText", "link", "tooltip", "children", "error", "touched", "htmlFor"];
var Field = _ref => {
  var {
      size = 'sm',
      label,
      isRequired = true,
      assistiveText,
      link,
      tooltip,
      children,
      error,
      touched,
      htmlFor
    } = _ref,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var hasCustomLabelProps = label || !isRequired || assistiveText || link || tooltip;
  if (!htmlFor || htmlFor === '') {
    htmlFor = isChromatic() ? '123' : cuid();
  }
  var childrenWithCustomId = /*#__PURE__*/React.isValidElement(children) ? /*#__PURE__*/React.cloneElement(children, {
    id: htmlFor
  }) : '';
  return /*#__PURE__*/jsx(FieldWrapper, _objectSpread2(_objectSpread2({}, otherProps), {}, {
    children: /*#__PURE__*/jsxs(Fragment, {
      children: [hasCustomLabelProps && /*#__PURE__*/jsxs(LabelField, {
        htmlFor: htmlFor,
        children: [/*#__PURE__*/jsx(Label, {
          size: size,
          label: label,
          isRequired: isRequired,
          assistiveText: assistiveText,
          link: link
        }), tooltip && /*#__PURE__*/jsx(Tooltip, {
          content: tooltip,
          position: "right",
          children: /*#__PURE__*/jsx(QuestionIcon, {})
        })]
      }), childrenWithCustomId, touched && error && /*#__PURE__*/jsx(ErrorMessage, {
        error: error
      })]
    })
  }));
};

export { Field, Field as default };
